import { CompleteKundali, Language, PlanetName, ZodiacSign } from '../types/kundali';
import { QualityZoneDistribution, QualityZone } from '../types/v517';
import { ZODIAC_LORDS } from './vedicEngine';

/* ==========================================================================
   VEDANG JYOTISHAM V5.17 — CREATOR / PRESERVER / TRANSFORMER QUALITY ENGINE
   Deterministic House-Quadrant Distribution & Rashi-Specific Manifestations
   ========================================================================== */

export const RASHI_QUALITY_MANIFESTATIONS: Record<
  ZodiacSign,
  Record<
    QualityZone,
    {
      archetypeTitle: string;
      archetypeTitleHi: string;
      coreTrait: string;
      coreTraitHi: string;
      behavioralStyle: string;
      behavioralStyleHi: string;
      optimalEnvironment: string;
      optimalEnvironmentHi: string;
      potentialPitfall: string;
      potentialPitfallHi: string;
    }
  >
> = {
  Aries: {
    CREATOR: {
      archetypeTitle: 'Pioneering Catalyst',
      archetypeTitleHi: 'अग्रणी सृजक (Pioneering Catalyst)',
      coreTrait: 'Fierce initiation, unstoppable drive, direct problem solving.',
      coreTraitHi: 'तीव्र नवाचार, अदम्य ऊर्जा, प्रत्यक्ष समस्या निवारण।',
      behavioralStyle: 'Leads from the front, launches ventures with bold decisive action.',
      behavioralStyleHi: 'अग्रिम पंक्ति से नेतृत्व, साहसिक व त्वरित निर्णय।',
      optimalEnvironment: 'Fast-paced, entrepreneurial, autonomous ventures.',
      optimalEnvironmentHi: 'गतिशील, उद्यमी व स्वायत्त कार्यक्षेत्र।',
      potentialPitfall: 'Impatience with maintenance phase after launch.',
      potentialPitfallHi: 'प्रारंभ के पश्चात निरंतरता में अधीरता।'
    },
    PRESERVER: {
      archetypeTitle: 'Guardian of Fortresses',
      archetypeTitleHi: 'दृढ़ संरक्षक (Guardian of Fortresses)',
      coreTrait: 'Courageous defense, structured action, relentless loyalty.',
      coreTraitHi: 'साहसी रक्षा, अनुशासित क्रियाशीलता, अटूट निष्ठा।',
      behavioralStyle: 'Protects assets, maintains operational discipline with vigor.',
      behavioralStyleHi: 'संसाधनों की सुरक्षा, अनुशासित संचालन।',
      optimalEnvironment: 'Security, operational oversight, crisis management.',
      optimalEnvironmentHi: 'सुरक्षा, परिचालन नियंत्रण, संकट प्रबंधन।',
      potentialPitfall: 'Rigidity under sudden changing circumstances.',
      potentialPitfallHi: 'अचानक बदलते परिदृश्यों में कठोरता।'
    },
    TRANSFORMER: {
      archetypeTitle: 'Revolutionary Phoenix',
      archetypeTitleHi: 'क्रांतिकारी नवीनीकरणकर्ता (Revolutionary Phoenix)',
      coreTrait: 'Fearless destruction of obsolete forms, rapid rebirth.',
      coreTraitHi: 'पुरातन व्यवस्था का निर्भय परिमार्जन, तीव्र पुनर्निर्माण।',
      behavioralStyle: 'Drives radical reforms, embraces high-stakes breakthroughs.',
      behavioralStyleHi: 'क्रांतिकारी सुधार, उच्च-दांव वाले बदलावों को अपनाना।',
      optimalEnvironment: 'Turnaround scenarios, transformative leadership.',
      optimalEnvironmentHi: 'कायापलट वाले परिदृश्य, परिवर्तनकारी नेतृत्व।',
      potentialPitfall: 'Over-disruption before foundations solidify.',
      potentialPitfallHi: 'नींव मजबूत होने से पूर्व अत्यधिक विघटन।'
    },
    BALANCED: {
      archetypeTitle: 'Integrated Trailblazer',
      archetypeTitleHi: 'समन्वित पथ-प्रदर्शक (Integrated Trailblazer)',
      coreTrait: 'Balanced dynamism, agile execution, versatile mastery.',
      coreTraitHi: 'संतुलित गतिशीलता, कुशल निष्पादन, बहुमुखी सामर्थ्य।',
      behavioralStyle: 'Balances bold beginnings with steady delivery and renewal.',
      behavioralStyleHi: 'साहसिक आरंभ, निरंतर निष्पादन एवं नवीनीकरण का संतुलन।',
      optimalEnvironment: 'Multidisciplinary leadership and strategic ventures.',
      optimalEnvironmentHi: 'बहुआयामी नेतृत्व व रणनीतिक उद्यम।',
      potentialPitfall: 'Diluting focus across too many simultaneous fronts.',
      potentialPitfallHi: 'एक साथ कई क्षेत्रों में ऊर्जा का विखंडन।'
    }
  },
  Taurus: {
    CREATOR: {
      archetypeTitle: 'Architect of Enduring Value',
      archetypeTitleHi: 'चिरस्थायी मूल्य निर्माता (Architect of Enduring Value)',
      coreTrait: 'Tangible creation, aesthetic mastery, pragmatic foundation building.',
      coreTraitHi: 'ठोस सृजन, सौंदर्यबोध, व्यावहारिक आधार निर्माण।',
      behavioralStyle: 'Builds systems, crafts material wealth with patience.',
      behavioralStyleHi: 'धैर्यपूर्वक संपदा व सुदृढ़ प्रणालियों का निर्माण।',
      optimalEnvironment: 'Finance, design, physical infrastructure, luxury arts.',
      optimalEnvironmentHi: 'वित्त, डिज़ाइन, भौतिक आधारभूत संरचना, कला।',
      potentialPitfall: 'Over-deliberation before taking the first step.',
      potentialPitfallHi: 'प्रारंभिक कदम उठाने में अत्यधिक विलंब।'
    },
    PRESERVER: {
      archetypeTitle: 'Pillar of Timeless Wealth',
      archetypeTitleHi: 'सदाबहार समृद्धि स्तंभ (Pillar of Timeless Wealth)',
      coreTrait: 'Unshakable stability, asset preservation, steadfast loyalty.',
      coreTraitHi: 'अडिग स्थिरता, संपत्ति संरक्षण, निष्ठावान समर्थन।',
      behavioralStyle: 'Maintains standards, nurtures long-term investments flawlessly.',
      behavioralStyleHi: 'मानकों का संरक्षण, दीर्घकालिक निवेशों का उत्कृष्ट पोषण।',
      optimalEnvironment: 'Wealth management, institutional stewardship, agriculture.',
      optimalEnvironmentHi: 'धन प्रबंधन, संस्थागत संचालन, संवर्धन।',
      potentialPitfall: 'Reluctance to adapt when structural shifts occur.',
      potentialPitfallHi: 'परिवर्तन के समय अनुकूलन में हिचकिचाहट।'
    },
    TRANSFORMER: {
      archetypeTitle: 'Alchemical Value Rebuilder',
      archetypeTitleHi: 'मूल्य-पुनरुद्धारक (Alchemical Value Rebuilder)',
      coreTrait: 'Transforming dormant resources into high-value assets.',
      coreTraitHi: 'सुप्त संसाधनों को मूल्यवान संपदा में रूपांतरित करना।',
      behavioralStyle: 'Restructures broken finances, revives neglected value.',
      behavioralStyleHi: 'रुके हुए वित्त का पुनर्गठन, उपेक्षित संसाधनों का पुनरुद्धार।',
      optimalEnvironment: 'Asset restructuring, legacy liquidation, renewal.',
      optimalEnvironmentHi: 'संपत्ति पुनर्गठन, पुनरुद्धार परियोजनाएं।',
      potentialPitfall: 'Holding on until the last possible moment.',
      potentialPitfallHi: 'अंतिम क्षण तक त्यागने में संकोच।'
    },
    BALANCED: {
      archetypeTitle: 'Harmonious Builder',
      archetypeTitleHi: 'संतुलित समृद्धिकर्ता (Harmonious Builder)',
      coreTrait: 'Steady creation combined with prudent risk management.',
      coreTraitHi: 'स्थिर सृजन एवं सतर्क जोखिम प्रबंधन का समन्वय।',
      behavioralStyle: 'Creates with elegance, sustains with calm resolve.',
      behavioralStyleHi: 'सौंदर्य के साथ निर्माण, शांत संकल्प के साथ पोषण।',
      optimalEnvironment: 'Sustainable enterprises and cultural institutions.',
      optimalEnvironmentHi: 'दीर्घकालिक उद्यम व सांस्कृतिक संस्थान।',
      potentialPitfall: 'Complacency during periods of apparent safety.',
      potentialPitfallHi: 'अत्यधिक सहजता में शिथिलता।'
    }
  },
  Gemini: {
    CREATOR: {
      archetypeTitle: 'Visionary Idea Generator',
      archetypeTitleHi: 'विचार-सृजक (Visionary Idea Generator)',
      coreTrait: 'Prolific brainstorming, intellectual curiosity, rapid prototyping.',
      coreTraitHi: 'विपुल विचार-मंथन, बौद्धिक जिज्ञासा, तीव्र प्रारूप निर्माण।',
      behavioralStyle: 'Sparks multiple innovative concepts, synthesizes diverse fields.',
      behavioralStyleHi: 'विविध क्षेत्रों के विचारों को जोड़कर नए सूत्र गढ़ना।',
      optimalEnvironment: 'Media, tech innovation, communications, journalism.',
      optimalEnvironmentHi: 'मीडिया, तकनीकी नवाचार, संचार, पत्रकारिता।',
      potentialPitfall: 'Scattered energy across too many unfinished ideas.',
      potentialPitfallHi: 'अनेक अधूरे विचारों में ऊर्जा का बिखराव।'
    },
    PRESERVER: {
      archetypeTitle: 'Knowledge Curator',
      archetypeTitleHi: 'ज्ञान संरक्षक (Knowledge Curator)',
      coreTrait: 'Systematic documentation, information networking, steady syndication.',
      coreTraitHi: 'व्यवस्थित प्रलेखन, सूचना नेटवर्किंग, निरंतर संप्रेषण।',
      behavioralStyle: 'Organizes knowledge archives, maintains communication channels.',
      behavioralStyleHi: 'ज्ञान अभिलेखागारों का प्रबंधन, संचार माध्यमों का संचालन।',
      optimalEnvironment: 'Publishing, education, technical writing, network management.',
      optimalEnvironmentHi: 'प्रकाशन, शिक्षा, तकनीकी लेखन, संचार प्रबंधन।',
      potentialPitfall: 'Information paralysis without tangible execution.',
      potentialPitfallHi: 'सूचना संकलन में उलझकर क्रियान्वयन में कमी।'
    },
    TRANSFORMER: {
      archetypeTitle: 'Disruptive Intellectual',
      archetypeTitleHi: 'वैचारिक कायाकल्पकर्ता (Disruptive Intellectual)',
      coreTrait: 'Challenging intellectual dogmas, paradigm shifting synthesis.',
      coreTraitHi: 'रूढ़िवादी धारणाओं को चुनौती, वैचारिक क्रांति।',
      behavioralStyle: 'Debunks obsolete theories, pivots communication narratives.',
      behavioralStyleHi: 'पुरातन मान्यताओं का खंडन, नई संचार दिशा गढ़ना।',
      optimalEnvironment: 'Strategic consulting, investigative journalism, narrative pivot.',
      optimalEnvironmentHi: 'रणनीतिक परामर्श, अन्वेषणात्मक पत्रकारिता।',
      potentialPitfall: 'Cynicism or excessive argumentative friction.',
      potentialPitfallHi: 'संशयवाद या अनावश्यक वाद-विवाद।'
    },
    BALANCED: {
      archetypeTitle: 'Master Communicator',
      archetypeTitleHi: 'कुशल संचारक (Master Communicator)',
      coreTrait: 'Fluent ideation, methodical delivery, and timely adaptations.',
      coreTraitHi: 'सहज विचार-सृजन, व्यवस्थित संप्रेषण एवं समयोचित अनुकूलन।',
      behavioralStyle: 'Bridges thinkers, operators, and reformists seamlessly.',
      behavioralStyleHi: 'विचारकों, प्रबंधकों व सुधारकों के मध्य सेतु निर्माण।',
      optimalEnvironment: 'Public relations, diplomacy, multimedia leadership.',
      optimalEnvironmentHi: 'जनसंपर्क, कूटनीति, मीडिया नेतृत्व।',
      potentialPitfall: 'Superficial treatment of profound emotional issues.',
      potentialPitfallHi: 'गहरे भावनात्मक विषयों की सतही अनदेखी।'
    }
  },
  Cancer: {
    CREATOR: {
      archetypeTitle: 'Nurturing Originator',
      archetypeTitleHi: 'स्नेहशील सूत्रधार (Nurturing Originator)',
      coreTrait: 'Intuitive ideation, emotional empathy, community building.',
      coreTraitHi: 'सहज अंतर्ज्ञान, भावनात्मक सहानुभूति, समुदाय निर्माण।',
      behavioralStyle: 'Creates spaces of belonging, originates heartfelt endeavors.',
      behavioralStyleHi: 'आत्मिक संबंधों का सृजन, संवेदनापूर्ण पहल।',
      optimalEnvironment: 'Healthcare, community founding, hospitality, counseling.',
      optimalEnvironmentHi: 'स्वास्थ्य सेवा, सामाजिक संस्थाएं, परामर्श।',
      potentialPitfall: 'Emotional vulnerability during early project friction.',
      potentialPitfallHi: 'आरंभिक विरोध में अत्यधिक भावुकता।'
    },
    PRESERVER: {
      archetypeTitle: 'Sanctuary Guardian',
      archetypeTitleHi: 'आश्रय-संरक्षक (Sanctuary Guardian)',
      coreTrait: 'Deep protective devotion, cultural preservation, emotional safety.',
      coreTraitHi: 'संरक्षण भाव, सांस्कृतिक धरोहर की रक्षा, सुरक्षा का वातावरण।',
      behavioralStyle: 'Protects family, traditions, and core assets with steadfast care.',
      behavioralStyleHi: 'परिवार, परंपराओं व मुख्य संपदा का निष्ठापूर्वक पोषण।',
      optimalEnvironment: 'Family enterprises, social welfare, heritage management.',
      optimalEnvironmentHi: 'पारिवारिक उद्यम, सामाजिक कल्याण, धरोहर संरक्षण।',
      potentialPitfall: 'Clinging to comfortable past paradigms.',
      potentialPitfallHi: 'अतीत की सहजता से चिपके रहना।'
    },
    TRANSFORMER: {
      archetypeTitle: 'Emotional Healer & Rebuilder',
      archetypeTitleHi: 'भावनात्मक कायाकल्पकर्ता (Emotional Healer & Rebuilder)',
      coreTrait: 'Transmuting pain into strength, profound emotional renewal.',
      coreTraitHi: 'कठिनाइयों को शक्ति में बदलना, गहन भावनात्मक नवीनीकरण।',
      behavioralStyle: 'Rebuilds broken relationships, guides through deep emotional rebirth.',
      behavioralStyleHi: 'टूटे संबंधों का पुनरुद्धार, संकट के समय आत्मिक मार्गदर्शन।',
      optimalEnvironment: 'Trauma counseling, philanthropic turnaround, palliative arts.',
      optimalEnvironmentHi: 'मानसिक स्वास्थ्य परामर्श, सामाजिक पुनरुद्धार।',
      potentialPitfall: 'Absorbing external negative emotional turbulence.',
      potentialPitfallHi: 'दूसरों की नकारात्मक ऊर्जा से स्वयं प्रभावित होना।'
    },
    BALANCED: {
      archetypeTitle: 'Compassionate Anchor',
      archetypeTitleHi: 'सहानुभूतिपूर्ण संबल (Compassionate Anchor)',
      coreTrait: 'Warm inspiration with grounded protective wisdom.',
      coreTraitHi: 'आत्मीय प्रेरणा एवं संतुलित सुरक्षात्मक विवेक का संगम।',
      behavioralStyle: 'Guides others with emotional intelligence and resilience.',
      behavioralStyleHi: 'भावनात्मक प्रज्ञा व धैर्य के साथ मार्गदर्शन।',
      optimalEnvironment: 'Educational administration, psychology, holistic healthcare.',
      optimalEnvironmentHi: 'शैक्षिक प्रशासन, मनोविज्ञान, समग्र स्वास्थ्य।',
      potentialPitfall: 'Defensive withdrawal under harsh criticism.',
      potentialPitfallHi: 'आलोचना पर आत्म-रक्षात्मक अलगाव।'
    }
  },
  Leo: {
    CREATOR: {
      archetypeTitle: 'Sovereign Visionary',
      archetypeTitleHi: 'प्रतापी सृजक (Sovereign Visionary)',
      coreTrait: 'Magnificent vision, creative brilliance, inspiring charisma.',
      coreTraitHi: 'भव्य दृष्टिकोण, रचनात्मक प्रतिभा, प्रेरक तेज।',
      behavioralStyle: 'Launches ambitious grand-scale projects with regal confidence.',
      behavioralStyleHi: 'भव्य आत्मविश्वास के साथ महत्वाकांक्षी योजनाओं का शुभारंभ।',
      optimalEnvironment: 'Executive leadership, entertainment, brand founding.',
      optimalEnvironmentHi: 'शीर्ष नेतृत्व, कला व सिनेमा, ब्रांड निर्माण।',
      potentialPitfall: 'Underestimating operational detail in favor of glamour.',
      potentialPitfallHi: 'भव्यता के आकर्षण में सूक्ष्म विवरणों की अनदेखी।'
    },
    PRESERVER: {
      archetypeTitle: 'Magnanimous Protector',
      archetypeTitleHi: 'उदार संरक्षक (Magnanimous Protector)',
      coreTrait: 'Benevolent authority, noble loyalty, institutional dignity.',
      coreTraitHi: 'कल्याणकारी अधिकार, उदात्त निष्ठा, संस्थागत गरिमा।',
      behavioralStyle: 'Maintains honor, shields subordinates, upholds standards.',
      behavioralStyleHi: 'सम्मान की रक्षा, अधीनस्थों का संबल, गरिमामय संचालन।',
      optimalEnvironment: 'Governance, corporate stewardship, royal foundations.',
      optimalEnvironmentHi: 'शासन-प्रशासन, कॉर्पोरेट प्रबंधन, सामाजिक ट्रस्ट।',
      potentialPitfall: 'Pride hindering necessary tactical compromises.',
      potentialPitfallHi: 'अहंकार के कारण रणनीतिक समझौते में बाधा।'
    },
    TRANSFORMER: {
      archetypeTitle: 'Epochal Reformer',
      archetypeTitleHi: 'युगान्तकारी सुधारक (Epochal Reformer)',
      coreTrait: 'Noble purge of corruption, courageous systemic overhaul.',
      coreTraitHi: 'भ्रष्ट व्यवस्था का शुद्धिकरण, साहसिक संस्थागत परिमार्जन।',
      behavioralStyle: 'Dismantles weak hierarchies to establish higher greatness.',
      behavioralStyleHi: 'दुर्बल व्यवस्थाओं को तोड़कर उत्कृष्ट संरचना स्थापित करना।',
      optimalEnvironment: 'Political reform, corporate turnaround, visionary renaissance.',
      optimalEnvironmentHi: 'राजनीतिक सुधार, संकटग्रस्त संस्थाओं का पुनरुद्धार।',
      potentialPitfall: 'Dramatic confrontation provoking prolonged opposition.',
      potentialPitfallHi: 'अत्यधिक आक्रामकता से दीर्घकालिक विरोध उत्पन्न होना।'
    },
    BALANCED: {
      archetypeTitle: 'Enlightened Monarch',
      archetypeTitleHi: 'प्रबुद्ध मार्गदर्शक (Enlightened Monarch)',
      coreTrait: 'Inspiring warmth, steady majesty, and visionary clarity.',
      coreTraitHi: 'प्रेरक सौहार्द, स्थिर गरिमा एवं दूरदर्शी स्पष्टता।',
      behavioralStyle: 'Commands respect through generosity, execution, and fair justice.',
      behavioralStyleHi: 'उदारता, न्याय एवं कार्यकुशलता से आदर प्राप्त करना।',
      optimalEnvironment: 'High-level executive leadership and philanthropy.',
      optimalEnvironmentHi: 'उच्च-स्तरीय प्रशासनिक नेतृत्व व लोक-कल्याण।',
      potentialPitfall: 'Need for constant validation or applause.',
      potentialPitfallHi: 'निरंतर प्रशंसा व मान्यता की अपेक्षा।'
    }
  },
  Virgo: {
    CREATOR: {
      archetypeTitle: 'Precision Artisan',
      archetypeTitleHi: 'कुशल शिल्पकार (Precision Artisan)',
      coreTrait: 'Flawless design, meticulous engineering, practical utility.',
      coreTraitHi: 'सटीक योजना, सूक्ष्म तकनीकी दक्षता, व्यावहारिक उपयोगिता।',
      behavioralStyle: 'Designs highly refined tools, systems, and functional methods.',
      behavioralStyleHi: 'अत्यंत परिष्कृत प्रणालियों व व्यावहारिक पद्धतियों का सृजन।',
      optimalEnvironment: 'Software architecture, biotechnology, forensic science.',
      optimalEnvironmentHi: 'सॉफ्टवेयर आर्किटेक्चर, जैव प्रौद्योगिकी, फॉरेंसिक विज्ञान।',
      potentialPitfall: 'Perfectionism delaying product release.',
      potentialPitfallHi: 'अति-शुद्धतावाद के कारण कार्य में विलंब।'
    },
    PRESERVER: {
      archetypeTitle: 'Methodical Custodian',
      archetypeTitleHi: 'अनुशासित व्यवस्थापक (Methodical Custodian)',
      coreTrait: 'Exacting quality control, continuous optimization, quiet service.',
      coreTraitHi: 'कठोर गुणवत्ता नियंत्रण, निरंतर सुधार, मौन निष्ठा।',
      behavioralStyle: 'Maintains error-free systems, diagnoses anomalies instantly.',
      behavioralStyleHi: 'त्रुटिहीन प्रणाली का संचालन, सूक्ष्म विसंगतियों की त्वरित पहचान।',
      optimalEnvironment: 'Auditing, quality assurance, logistics, medical operations.',
      optimalEnvironmentHi: 'ऑडिटिंग, गुणवत्ता आश्वासन, रसद प्रबंधन, चिकित्सा।',
      potentialPitfall: 'Micro-management breeding team fatigue.',
      potentialPitfallHi: 'अत्यधिक सूक्ष्म-नियंत्रण से सहयोगियों में तनाव।'
    },
    TRANSFORMER: {
      archetypeTitle: 'Systemic Healer & Restructurer',
      archetypeTitleHi: 'व्यवस्था-सुधारक (Systemic Healer & Restructurer)',
      coreTrait: 'Root-cause diagnosis, elimination of inefficiency and waste.',
      coreTraitHi: 'मूल कारणों की पहचान, अपव्यय व अक्षमताओं का पूर्ण उन्मूलन।',
      behavioralStyle: 'Audits broken workflows, strips away fluff to restore vigor.',
      behavioralStyleHi: 'अव्यवस्थित कार्यप्रणाली को सुधारकर कार्यक्षमता बढ़ाना।',
      optimalEnvironment: 'Operational restructuring, process re-engineering.',
      optimalEnvironmentHi: 'परिचालन पुनर्गठन, प्रक्रिया पुनर्रचना।',
      potentialPitfall: 'Overly clinical critique offending sensitive colleagues.',
      potentialPitfallHi: 'अत्यधिक कठोर आलोचना से संबंधों में खटास।'
    },
    BALANCED: {
      archetypeTitle: 'Master Optimizer',
      archetypeTitleHi: 'दक्ष सूत्रधार (Master Optimizer)',
      coreTrait: 'Constructive ingenuity, steady efficiency, and pragmatic reform.',
      coreTraitHi: 'रचनात्मक सूझबूझ, निरंतर कार्यकुशलता एवं व्यावहारिक सुधार।',
      behavioralStyle: 'Solves complex puzzles calmly with empirical precision.',
      behavioralStyleHi: 'प्रमाणिक सटीकता के साथ जटिल समस्याओं का समाधान।',
      optimalEnvironment: 'Data science, financial analysis, clinical management.',
      optimalEnvironmentHi: 'डेटा साइंस, वित्तीय विश्लेषण, क्लिनिकल प्रबंधन।',
      potentialPitfall: 'Anxiety generated by uncontrollable external chaos.',
      potentialPitfallHi: 'अनियंत्रित बाह्य परिस्थितियों से मानसिक तनाव।'
    }
  },
  Libra: {
    CREATOR: {
      archetypeTitle: 'Aesthetic Diplomat',
      archetypeTitleHi: 'सौंदर्य-सूत्रधार (Aesthetic Diplomat)',
      coreTrait: 'Harmonious alliance building, design elegance, partnership creation.',
      coreTraitHi: 'सद्भावपूर्ण गठबंधन, सौंदर्यबोध, साझेदारी निर्माण।',
      behavioralStyle: 'Initiates partnerships, creates balanced agreements and art.',
      behavioralStyleHi: 'सहकार्यता की शुरुआत, संतुलित समझौतों का निर्माण।',
      optimalEnvironment: 'Law, mediation, interior architecture, diplomacy.',
      optimalEnvironmentHi: 'विधि, मध्यस्थता, कला व डिज़ाइन, कूटनीति।',
      potentialPitfall: 'Indecisiveness stemming from weighing too many options.',
      potentialPitfallHi: 'अनेक विकल्पों में उलझकर अनिर्णय की स्थिति।'
    },
    PRESERVER: {
      archetypeTitle: 'Keeper of Equilibrium',
      archetypeTitleHi: 'सद्भाव संरक्षक (Keeper of Equilibrium)',
      coreTrait: 'Fair adjudication, relationship maintenance, social justice.',
      coreTraitHi: 'निष्पक्ष न्याय, संबंधों का पोषण, सामाजिक संतुलन।',
      behavioralStyle: 'Sustains peace, manages consensus among conflicting parties.',
      behavioralStyleHi: 'शांति बनाए रखना, परस्पर विरोधी पक्षों में सहमति बनाना।',
      optimalEnvironment: 'Judiciary, human resources, diplomatic corps, fine arts.',
      optimalEnvironmentHi: 'न्यायपालिका, मानव संसाधन, कूटनीतिक संस्थाएं।',
      potentialPitfall: 'Compromising essential principles just to avoid discord.',
      potentialPitfallHi: 'विवाद टालने हेतु मूल सिद्धांतों से समझौता।'
    },
    TRANSFORMER: {
      archetypeTitle: 'Catalyst of Equity',
      archetypeTitleHi: 'न्याय-क्रांतिकारी (Catalyst of Equity)',
      coreTrait: 'Dismantling unfair agreements, recalibrating social justice.',
      coreTraitHi: 'अन्यायपूर्ण समझौतों को तोड़ना, न्याय का पुनर्संतुलन।',
      behavioralStyle: 'Overturns biased contracts, forces balanced renegotiation.',
      behavioralStyleHi: 'पक्षपाती अनुबंधों को निरस्त कर नए न्यायपूर्ण नियम बनाना।',
      optimalEnvironment: 'Litigation, human rights advocacy, trade renegotiation.',
      optimalEnvironmentHi: 'अधिकार संरक्षण, विधि संघर्ष, व्यापारिक पुनर्संवाद।',
      potentialPitfall: 'Emotional distress under bitter adversarial friction.',
      potentialPitfallHi: 'कटु संघर्षों में मानसिक अशांति।'
    },
    BALANCED: {
      archetypeTitle: 'Strategic Peacemaker',
      archetypeTitleHi: 'रणनीतिक शांतिदूत (Strategic Peacemaker)',
      coreTrait: 'Gracious influence, objective fairness, and win-win mastery.',
      coreTraitHi: 'गरिमामय प्रभाव, निष्पक्ष दृष्टिकोण एवं सर्व-सम्मत परिणाम।',
      behavioralStyle: 'Builds enduring bridges between diverse interests.',
      behavioralStyleHi: 'विविध पक्षों के मध्य स्थायी सामंजस्य स्थापित करना।',
      optimalEnvironment: 'International arbitration, cross-border commerce.',
      optimalEnvironmentHi: 'अंतरराष्ट्रीय मध्यस्थता, व्यापक व्यापारिक संबंध।',
      potentialPitfall: 'Over-extending energy to please everyone.',
      potentialPitfallHi: 'सभी को संतुष्ट करने के प्रयास में ऊर्जा का क्षरण।'
    }
  },
  Scorpio: {
    CREATOR: {
      archetypeTitle: 'Deep Occult Initiator',
      archetypeTitleHi: 'गूढ़ अनुसंधायक (Deep Occult Initiator)',
      coreTrait: 'Penetrative insight, intense focus, uncovering hidden secrets.',
      coreTraitHi: 'गहन अंतर्दृष्टि, एकाग्रता, गुप्त सत्यों का अन्वेषण।',
      behavioralStyle: 'Launches deep research ventures, probes the unseen frontiers.',
      behavioralStyleHi: 'गहन शोध की शुरुआत, अदृश्य रहस्यों की खोज।',
      optimalEnvironment: 'Cybersecurity, investigative science, deep technology.',
      optimalEnvironmentHi: 'साइबर सुरक्षा, अनुसंधान, गूढ़ विज्ञान।',
      potentialPitfall: 'Excessive secrecy alienating initial collaborators.',
      potentialPitfallHi: 'अत्यधिक गोपनीयता के कारण सहयोगियों में दूरी।'
    },
    PRESERVER: {
      archetypeTitle: 'Unfaltering Sentinel',
      archetypeTitleHi: 'अभेद रक्षक (Unfaltering Sentinel)',
      coreTrait: 'Absolute loyalty, crisis containment, impenetrable defense.',
      coreTraitHi: 'परम निष्ठा, संकट निवारण, अभेद्य सुरक्षा।',
      behavioralStyle: 'Guards confidential wealth and values with fierce vigilance.',
      behavioralStyleHi: 'गोपनीय संपदा व रहस्यों की दृढ़तापूर्वक रक्षा।',
      optimalEnvironment: 'Intelligence, risk containment, private equity holding.',
      optimalEnvironmentHi: 'खुफिया एजेंसियां, जोखिम प्रबंधन, निजी निवेश।',
      potentialPitfall: 'Suspicions damaging trusted long-term relationships.',
      potentialPitfallHi: 'अनावश्यक संदेह से घनिष्ठ संबंधों में दरार।'
    },
    TRANSFORMER: {
      archetypeTitle: 'Supreme Alchemist',
      archetypeTitleHi: 'परम रूपांतरणकर्ता (Supreme Alchemist)',
      coreTrait: 'Total metamorphosis, psychological resurrection, shedding the old.',
      coreTraitHi: 'संपूर्ण कायाकल्प, मनोवैज्ञानिक पुनरुत्थान, पुरातन का पूर्ण त्याग।',
      behavioralStyle: 'Thrives in total collapse, emerges stronger from ashes.',
      behavioralStyleHi: 'संकट में खिलना, विनाश से अप्रत्याशित शक्ति प्राप्त करना।',
      optimalEnvironment: 'Crisis rescue, deep therapy, extreme turnaround management.',
      optimalEnvironmentHi: 'संकट मोचन, गहन मनोविज्ञान, विषम परिस्थितियों का उद्धार।',
      potentialPitfall: 'Creating crises unconsciously to feel alive.',
      potentialPitfallHi: 'उत्तेजना हेतु अनजाने में संकट को जन्म देना।'
    },
    BALANCED: {
      archetypeTitle: 'Master of Depths',
      archetypeTitleHi: 'गूढ़ प्रज्ञावान (Master of Depths)',
      coreTrait: 'Silent power, profound wisdom, and transformative resilience.',
      coreTraitHi: 'मौन सामर्थ्य, गहन प्रज्ञा एवं अदम्य सहनशीलता।',
      behavioralStyle: 'Executes decisive moves with quiet precision and impact.',
      behavioralStyleHi: 'शांत सटीकता व प्रभाव के साथ निर्णायक कदम उठाना।',
      optimalEnvironment: 'Strategic leadership, advanced surgical medicine, espionage.',
      optimalEnvironmentHi: 'रणनीतिक नेतृत्व, शल्य चिकित्सा, अन्वेषण।',
      potentialPitfall: 'Difficulty letting go of past betrayals.',
      potentialPitfallHi: 'अतीत के आघातों को भुलाने में कठिनाई।'
    }
  },
  Sagittarius: {
    CREATOR: {
      archetypeTitle: 'Expansive Philosopher',
      archetypeTitleHi: 'विशाल दार्शनिक (Expansive Philosopher)',
      coreTrait: 'Grand ideals, boundless optimism, seeking new horizons.',
      coreTraitHi: 'उदात्त आदर्श, असीम आशावाद, नए क्षितिजों की खोज।',
      behavioralStyle: 'Sparks visionary movements, launches cross-cultural journeys.',
      behavioralStyleHi: 'वैचारिक आंदोलनों का शुभारंभ, ज्ञान यात्राएं।',
      optimalEnvironment: 'Higher education, international exploration, publishing.',
      optimalEnvironmentHi: 'उच्च शिक्षा, अंतरराष्ट्रीय अन्वेषण, वैचारिक मंच।',
      potentialPitfall: 'Over-committing without logistical grounding.',
      potentialPitfallHi: 'व्यावहारिक तैयारी के बिना अत्यधिक वादे करना।'
    },
    PRESERVER: {
      archetypeTitle: 'Dharmic Custodian',
      archetypeTitleHi: 'धर्म-संरक्षक (Dharmic Custodian)',
      coreTrait: 'Preserving ethical truths, wisdom traditions, universal principles.',
      coreTraitHi: 'नैतिक सत्यों की रक्षा, ज्ञान परंपराओं का संवर्धन।',
      behavioralStyle: 'Teaches timeless wisdom, guides institutions with moral authority.',
      behavioralStyleHi: 'शाश्वत ज्ञान का प्रसार, नैतिक अधिकार से मार्गदर्शन।',
      optimalEnvironment: 'Spiritual orders, universities, constitutional law.',
      optimalEnvironmentHi: 'आध्यात्मिक संस्थान, विश्वविद्यालय, विधि एवं न्याय।',
      potentialPitfall: 'Dogmatism when challenged by unorthodox views.',
      potentialPitfallHi: 'भिन्न विचारों के समक्ष हठधर्मिता।'
    },
    TRANSFORMER: {
      archetypeTitle: 'Prophetic Disruptor',
      archetypeTitleHi: 'क्रांतिकारी पथप्रदर्शक (Prophetic Disruptor)',
      coreTrait: 'Dismantling hypocrisy, shattering rigid dogma, truth seeking.',
      coreTraitHi: 'पाखंड का खंडन, संकीर्ण रूढ़ियों का निवारण, सत्य की प्रतिष्ठा।',
      behavioralStyle: 'Challenges corrupt beliefs, re-ignites spiritual revolutions.',
      behavioralStyleHi: 'भ्रामक मान्यताओं को तोड़कर सत्य की नई ज्योति जगाना।',
      optimalEnvironment: 'Philosophical reform, paradigm breaking research.',
      optimalEnvironmentHi: 'दार्शनिक सुधार, युगांतरकारी अनुसंधान।',
      potentialPitfall: 'Zealotry or lack of tact in blunt communication.',
      potentialPitfallHi: 'स्पष्टवादिता में संवेदनशीलता की कमी।'
    },
    BALANCED: {
      archetypeTitle: 'Enlightened Guide',
      archetypeTitleHi: 'प्रबुद्ध मार्गदर्शक (Enlightened Guide)',
      coreTrait: 'Expansive joy, moral integrity, and practical inspiration.',
      coreTraitHi: 'उमंग, नैतिक निष्ठा एवं व्यावहारिक प्रेरणा।',
      behavioralStyle: 'Uplifts people, connects high philosophy with human warmth.',
      behavioralStyleHi: 'जनमानस को प्रेरित करना, दर्शन को जनसुलभ बनाना।',
      optimalEnvironment: 'Global mentorship, ethical entrepreneurship, culture.',
      optimalEnvironmentHi: 'वैश्विक मार्गदर्शन, नैतिक उद्यम, सांस्कृतिक उत्थान।',
      potentialPitfall: 'Restlessness when confined to routine.',
      potentialPitfallHi: 'दैनिक दिनचर्या में बेचैनी का अनुभव।'
    }
  },
  Capricorn: {
    CREATOR: {
      archetypeTitle: 'Master Empire Builder',
      archetypeTitleHi: 'साम्राज्य निर्माता (Master Empire Builder)',
      coreTrait: 'Strategic long-range vision, monumental endurance, executive power.',
      coreTraitHi: 'दीर्घकालिक रणनीति, अभूतपूर्व धैर्य, प्रशासनिक दक्षता।',
      behavioralStyle: 'Establishes enduring corporations, institutions, and infrastructure.',
      behavioralStyleHi: 'चिरस्थायी संस्थाओं, उद्यमों व संरचनाओं की स्थापना।',
      optimalEnvironment: 'Heavy industry, conglomerates, government institutions.',
      optimalEnvironmentHi: 'उद्योग, बृहद कॉर्पोरेट, शासकीय संगठन।',
      potentialPitfall: 'Excessive gravity suppressing joyful spontaneity.',
      potentialPitfallHi: 'अत्यधिक गंभीरता के कारण सहज आनंद में कमी।'
    },
    PRESERVER: {
      archetypeTitle: 'Bedrock of Governance',
      archetypeTitleHi: 'शासन का सुदृढ़ आधार (Bedrock of Governance)',
      coreTrait: 'Unwavering duty, institutional discipline, impeccable reliability.',
      coreTraitHi: 'अटल कर्तव्यनिष्ठा, संस्थागत अनुशासन, पूर्ण विश्वसनीयता।',
      behavioralStyle: 'Safeguards legacy structures, ensures generational continuity.',
      behavioralStyleHi: 'धरोहर का संरक्षण, पीढ़ियों तक चलने वाली निरंतरता।',
      optimalEnvironment: 'Public administration, enterprise governance, banking.',
      optimalEnvironmentHi: 'लोक प्रशासन, संस्थागत प्रबंधन, बैंकिंग।',
      potentialPitfall: 'Resistance to necessary modernization.',
      potentialPitfallHi: 'आधुनिकीकरण के प्रति अनावश्यक प्रतिरोध।'
    },
    TRANSFORMER: {
      archetypeTitle: 'Structural Overhauler',
      archetypeTitleHi: 'संरचनात्मक कायाकल्पकर्ता (Structural Overhauler)',
      coreTrait: 'Ruthless efficiency, eliminating bureaucratic decay, structural rebirth.',
      coreTraitHi: 'कार्यकुशलता, लालफीताशाही का उन्मूलन, संगठनात्मक पुनर्जन्म।',
      behavioralStyle: 'Rebuilds bankrupt structures, enforces lean productivity.',
      behavioralStyleHi: 'अकुशल प्रणालियों को समाप्त कर उत्पादकता बढ़ाना।',
      optimalEnvironment: 'Corporate restructuring, bankruptcy turnaround, re-engineering.',
      optimalEnvironmentHi: 'कॉर्पोरेट पुनर्गठन, पुनरुद्धार प्रबंधन।',
      potentialPitfall: 'Cold austerity alienating the workforce.',
      potentialPitfallHi: 'अत्यधिक कठोरता से सहकर्मियों में अलगाव।'
    },
    BALANCED: {
      archetypeTitle: 'Pragmatic Sovereign',
      archetypeTitleHi: 'व्यावहारिक राजनेता (Pragmatic Sovereign)',
      coreTrait: 'Commanding stature, patient execution, and pragmatic wisdom.',
      coreTraitHi: 'प्रभावशाली व्यक्तित्व, धैर्यपूर्ण क्रियान्वयन व व्यावहारिक विवेक।',
      behavioralStyle: 'Builds step by step with quiet authority and integrity.',
      behavioralStyleHi: 'शांत अधिकार व निष्ठा के साथ चरणबद्ध निर्माण।',
      optimalEnvironment: 'Boardrooms, judicial governance, major engineering.',
      optimalEnvironmentHi: 'निदेशक मंडल, न्यायपालिका, बृहद इंजीनियरिंग।',
      potentialPitfall: 'Workaholism leading to burnout.',
      potentialPitfallHi: 'अत्यधिक कार्यभार से मानसिक व शारीरिक थकान।'
    }
  },
  Aquarius: {
    CREATOR: {
      archetypeTitle: 'Futuristic Inventor',
      archetypeTitleHi: 'भविष्यवादी आविष्कारक (Futuristic Inventor)',
      coreTrait: 'Revolutionary innovation, egalitarian vision, unconventional intellect.',
      coreTraitHi: 'क्रांतिकारी नवाचार, समतावादी दृष्टिकोण, विलक्षण बुद्धि।',
      behavioralStyle: 'Pioneers breakthrough technologies, social networks, new paradigms.',
      behavioralStyleHi: 'अभूतपूर्व तकनीकों, सामाजिक आंदोलनों व नए सूत्रों का सूत्रपात।',
      optimalEnvironment: 'AI & frontier tech, humanitarian networks, social platforms.',
      optimalEnvironmentHi: 'आर्टिफिशियल इंटेलिजेंस, अत्याधुनिक तकनीक, लोक-कल्याण नेटवर्क।',
      potentialPitfall: 'Detachment from immediate human feelings.',
      potentialPitfallHi: 'तात्कालिक मानवीय संवेदनाओं से अलगाव।'
    },
    PRESERVER: {
      archetypeTitle: 'Social Network Steward',
      archetypeTitleHi: 'सामूहिक मंच संरक्षक (Social Network Steward)',
      coreTrait: 'Fostering collective solidarity, democratic governance, broad welfare.',
      coreTraitHi: 'सामूहिक एकता, लोकतांत्रिक संरचना, सार्वभौमिक कल्याण।',
      behavioralStyle: 'Maintains communities, ensures equitable distribution of resources.',
      behavioralStyleHi: 'समुदायों का पोषण, संसाधनों का न्यायसंगत वितरण।',
      optimalEnvironment: 'NGOs, decentralized organizations, cooperative syndicates.',
      optimalEnvironmentHi: 'गैर-सरकारी संगठन, विकेंद्रीकृत संस्थाएं, सहकारी समितियां।',
      potentialPitfall: 'Dogmatic adherence to intellectual ideology.',
      potentialPitfallHi: 'वैचारिक सिद्धांतों के प्रति अत्यधिक हठ।'
    },
    TRANSFORMER: {
      archetypeTitle: 'Radical System Shifter',
      archetypeTitleHi: 'व्यवस्था-परिवर्तक (Radical System Shifter)',
      coreTrait: 'Abolishing oppressive hierarchies, democratizing power.',
      coreTraitHi: 'दमनकारी व्यवस्थाओं का उन्मूलन, शक्ति का लोकतंत्रीकरण।',
      behavioralStyle: 'Dismantles monopolies, triggers sudden collective awakenings.',
      behavioralStyleHi: 'एकाधिकार को तोड़ना, सामूहिक चेतना को जाग्रत करना।',
      optimalEnvironment: 'Open-source revolutions, collective activism, decentralized finance.',
      optimalEnvironmentHi: 'ओपन-सोर्स आंदोलन, सामाजिक जागृति, विकेंद्रीकृत वित्त।',
      potentialPitfall: 'Anarchic disruption without a stable replacement.',
      potentialPitfallHi: 'स्थायी विकल्प के बिना अराजक विघटन।'
    },
    BALANCED: {
      archetypeTitle: 'Humanitarian Pioneer',
      archetypeTitleHi: 'मानवतावादी पथप्रदर्शक (Humanitarian Pioneer)',
      coreTrait: 'Universal empathy, scientific clarity, and social idealism.',
      coreTraitHi: 'वैश्विक संवेदना, वैज्ञानिक स्पष्टता एवं सामाजिक आदर्शवाद।',
      behavioralStyle: 'Unites diverse groups toward visionary collective progress.',
      behavioralStyleHi: 'विविध वर्गों को सामूहिक उन्नति हेतु एकजुट करना।',
      optimalEnvironment: 'Global policy, scientific collectives, humanitarian funds.',
      optimalEnvironmentHi: 'वैश्विक नीतियां, वैज्ञानिक संस्थान, लोक-हितकारी ट्रस्ट।',
      potentialPitfall: 'Stubborn eccentricity under conformist pressure.',
      potentialPitfallHi: 'रूढ़िवादिता के दबाव में जिद्दी अलगाव।'
    }
  },
  Pisces: {
    CREATOR: {
      archetypeTitle: 'Mystic Artist & Dreamer',
      archetypeTitleHi: 'अतींद्रिय साधक व स्वप्नद्रष्टा (Mystic Artist & Dreamer)',
      coreTrait: 'Boundless imagination, transcendental inspiration, spiritual beauty.',
      coreTraitHi: 'असीम कल्पनाशीलता, दिव्य प्रेरणा, आध्यात्मिक सौंदर्य।',
      behavioralStyle: 'Channels divine inspiration into art, music, and spiritual paths.',
      behavioralStyleHi: 'दिव्य प्रेरणा को कला, संगीत व साधना में ढालना।',
      optimalEnvironment: 'Creative arts, spiritual sanctuaries, immersive media.',
      optimalEnvironmentHi: 'सृजनात्मक कलाएं, आध्यात्मिक केंद्र, संगीत।',
      potentialPitfall: 'Escapism and lack of material boundaries.',
      potentialPitfallHi: 'व्यावहारिक सीमाओं व यथार्थ से पलायन।'
    },
    PRESERVER: {
      archetypeTitle: 'Oceanic Compassion Custodian',
      archetypeTitleHi: 'अनंत करुणा संरक्षक (Oceanic Compassion Custodian)',
      coreTrait: 'Universal forgiveness, selfless dedication, healing presence.',
      coreTraitHi: 'सार्वभौमिक क्षमाशीलता, निःस्वार्थ समर्पण, उपचारात्मक उपस्थिति।',
      behavioralStyle: 'Maintains sanctuaries of peace, comforts suffering souls quietly.',
      behavioralStyleHi: 'शांति के केंद्रों का संरक्षण, पीड़ितों को आत्मिक संबल।',
      optimalEnvironment: 'Hospices, ashrams, philanthropic shelters, meditation centers.',
      optimalEnvironmentHi: 'आश्रम, सेवा सदन, ध्यान केंद्र, पुनर्वास।',
      potentialPitfall: 'Boundary dissolution leading to personal depletion.',
      potentialPitfallHi: 'सीमाओं के अभाव में स्वयं का मानसिक क्षरण।'
    },
    TRANSFORMER: {
      archetypeTitle: 'Spiritual Liberator',
      archetypeTitleHi: 'मोक्ष-प्रदाता (Spiritual Liberator)',
      coreTrait: 'Dissolving ego illusions, facilitating ultimate spiritual release.',
      coreTraitHi: 'अहंकार के भ्रम का विलय, परम आध्यात्मिक मुक्ति का मार्ग।',
      behavioralStyle: 'Guides through ego death, releases deep karmic entanglements.',
      behavioralStyleHi: 'कर्म बंधनों से मुक्ति, गहन आध्यात्मिक जागरण।',
      optimalEnvironment: 'Moksha counseling, mystical initiation, renunciant guidance.',
      optimalEnvironmentHi: 'आध्यात्मिक दीक्षा, वैराग्य मार्गदर्शन, योग साधना।',
      potentialPitfall: 'Disillusionment with the mundane physical world.',
      potentialPitfallHi: 'सांसारिक जीवन के प्रति विरक्ति व उदासीनता।'
    },
    BALANCED: {
      archetypeTitle: 'Transcendental Sage',
      archetypeTitleHi: 'तत्वज्ञानी मनीषी (Transcendental Sage)',
      coreTrait: 'Luminous compassion, profound intuition, and gentle strength.',
      coreTraitHi: 'तेजोमय करुणा, गहन अंतर्ज्ञान एवं सौम्य सामर्थ्य।',
      behavioralStyle: 'Navigates physical reality while anchored in higher consciousness.',
      behavioralStyleHi: 'उच्च चेतना में स्थित रहकर सांसारिक कर्तव्यों का निर्वहन।',
      optimalEnvironment: 'Spiritual leadership, profound artistic direction, counseling.',
      optimalEnvironmentHi: 'आध्यात्मिक नेतृत्व, कला निर्देशन, आत्मिक परामर्श।',
      potentialPitfall: 'Over-sensitivity to discordant environments.',
      potentialPitfallHi: 'अशांत वातावरण से अत्यधिक संवेदनशीलता।'
    }
  }
};

/**
 * Calculates the Creator / Preserver / Transformer distribution for any Kundali
 */
export function calculateQualityDistribution(
  kundali: CompleteKundali,
  lang: Language = 'en'
): QualityZoneDistribution {
  const isHi = lang === 'hi';
  const lagnaHouse = kundali.houses[0];
  const lagnaSign = (lagnaHouse?.sign || 'Aries') as ZodiacSign;
  const lagnaLord = (ZODIAC_LORDS[lagnaSign] || 'Mars') as PlanetName;

  // 9 Vedic Grahas
  const classicalPlanets: PlanetName[] = ['Sun', 'Moon', 'Mars', 'Mercury', 'Jupiter', 'Venus', 'Saturn', 'Rahu', 'Ketu'];

  const creatorPlanets: PlanetName[] = [];
  const preserverPlanets: PlanetName[] = [];
  const transformerPlanets: PlanetName[] = [];

  for (const pName of classicalPlanets) {
    const pObj = kundali.planets.find(p => p.name === pName);
    if (!pObj) continue;
    const h = pObj.house;
    if (h >= 1 && h <= 4) {
      creatorPlanets.push(pName);
    } else if (h >= 5 && h <= 8) {
      preserverPlanets.push(pName);
    } else if (h >= 9 && h <= 12) {
      transformerPlanets.push(pName);
    }
  }

  const total = creatorPlanets.length + preserverPlanets.length + transformerPlanets.length || 9;
  const cPct = Math.round((creatorPlanets.length / total) * 100);
  const pPct = Math.round((preserverPlanets.length / total) * 100);
  const tPct = Math.round((transformerPlanets.length / total) * 100);

  // Determine Dominant & Secondary with Tie-Break Rule
  let dominant: QualityZone = 'CREATOR';
  let secondary: QualityZone | 'NONE' = 'NONE';
  let isBalanced = false;
  let tieBreakApplied = false;
  let tieBreakReason = '';

  const counts: { zone: QualityZone; count: number }[] = [
    { zone: 'CREATOR', count: creatorPlanets.length },
    { zone: 'PRESERVER', count: preserverPlanets.length },
    { zone: 'TRANSFORMER', count: transformerPlanets.length }
  ];

  counts.sort((a, b) => b.count - a.count);

  if (counts[0].count === counts[1].count) {
    // Tie-break rule:
    // 1st Priority: Placement zone of Lagna Lord
    // 2nd Priority: Placement zone of Moon
    // 3rd Priority: Placement zone of Sun
    tieBreakApplied = true;
    const lordObj = kundali.planets.find(p => p.name === lagnaLord);
    const moonObj = kundali.planets.find(p => p.name === 'Moon');
    const sunObj = kundali.planets.find(p => p.name === 'Sun');

    const getZoneOfHouse = (h: number): QualityZone => {
      if (h >= 1 && h <= 4) return 'CREATOR';
      if (h >= 5 && h <= 8) return 'PRESERVER';
      return 'TRANSFORMER';
    };

    const lordZone = lordObj ? getZoneOfHouse(lordObj.house) : 'CREATOR';
    const moonZone = moonObj ? getZoneOfHouse(moonObj.house) : 'CREATOR';
    const sunZone = sunObj ? getZoneOfHouse(sunObj.house) : 'CREATOR';

    if (counts[0].count === counts[2].count) {
      // 3-way tie (3, 3, 3)
      isBalanced = true;
      dominant = 'BALANCED';
      secondary = lordZone;
      tieBreakReason = `Equal 3-way distribution (33% across all zones). Balanced Quality resolved with Lagna Lord (${lagnaLord}) in ${lordZone} zone.`;
    } else {
      // 2-way tie
      const tiedZones = [counts[0].zone, counts[1].zone];
      if (tiedZones.includes(lordZone)) {
        dominant = lordZone;
        secondary = tiedZones.find(z => z !== lordZone) || 'NONE';
        tieBreakReason = `Tie broken by Lagna Lord (${lagnaLord}) located in ${lordZone} zone (House ${lordObj?.house}).`;
      } else if (tiedZones.includes(moonZone)) {
        dominant = moonZone;
        secondary = tiedZones.find(z => z !== moonZone) || 'NONE';
        tieBreakReason = `Tie broken by Moon (Chandra) located in ${moonZone} zone (House ${moonObj?.house}).`;
      } else {
        dominant = sunZone;
        secondary = tiedZones.find(z => z !== sunZone) || 'NONE';
        tieBreakReason = `Tie broken by Sun (Surya) located in ${sunZone} zone.`;
      }
    }
  } else {
    dominant = counts[0].zone;
    secondary = counts[1].count > 0 ? counts[1].zone : 'NONE';
    if (Math.abs(cPct - pPct) <= 15 && Math.abs(pPct - tPct) <= 15) {
      isBalanced = true;
    }
  }

  const rashiData = RASHI_QUALITY_MANIFESTATIONS[lagnaSign] || RASHI_QUALITY_MANIFESTATIONS.Aries;
  const manifestation = rashiData[dominant] || rashiData.CREATOR;

  return {
    creator: {
      zoneName: 'Creator-Oriented Zone (Houses 1–4)',
      zoneNameHi: 'सृजन क्षेत्र (भाव 1–4)',
      houseRange: '1–4',
      planets: creatorPlanets,
      planetCount: creatorPlanets.length,
      percentage: cPct,
      description: 'Initiation, generation, ideation, pioneering drive, and new beginnings.',
      descriptionHi: 'नवाचार, विचार-सृजन, आरंभिक ऊर्जा एवं नवनिर्माण।'
    },
    preserver: {
      zoneName: 'Preserver-Oriented Zone (Houses 5–8)',
      zoneNameHi: 'संरक्षण क्षेत्र (भाव 5–8)',
      houseRange: '5–8',
      planets: preserverPlanets,
      planetCount: preserverPlanets.length,
      percentage: pPct,
      description: 'Stability, continuity, nurturing, protection, and structural maintenance.',
      descriptionHi: 'स्थिरता, निरंतरता, पोषण, सुरक्षा एवं संगठनात्मक संवर्धन।'
    },
    transformer: {
      zoneName: 'Transformer-Oriented Zone (Houses 9–12)',
      zoneNameHi: 'रूपांतरण क्षेत्र (भाव 9–12)',
      houseRange: '9–12',
      planets: transformerPlanets,
      planetCount: transformerPlanets.length,
      percentage: tPct,
      description: 'Transformation, dissolution, spiritual renewal, elevation, and radical change.',
      descriptionHi: 'कायाकल्प, आध्यात्मिक उत्थान, नवीनीकरण एवं युगांतरकारी रूपांतरण।'
    },
    dominantZone: dominant,
    secondaryZone: secondary,
    isBalanced,
    tieBreakApplied,
    tieBreakReason: tieBreakReason || undefined,
    rashiSpecificManifestation: {
      rashi: lagnaSign,
      ...manifestation
    }
  };
}
